// SignedDocumentViewerDialog — D2 (S1): shows a PDF (the signed document or the issued reference
// side of the pairing, CU1/CU2) inline from a blob, in a MUI dialog. The JWT travels through the
// Authorization header (Api_Functions), so the PDF cannot be opened in a raw new tab with a plain
// URL; this dialog fetches the binary itself and renders it in an <iframe> against a local blob
// URL. Read-only (RN1/RN9): it never writes, never transitions the acceptance state.
import { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { fetchAcceptanceDocumentContent } from './acceptanceDocumentContent'

export default function SignedDocumentViewerDialog ({ open, onClose, title, contentPath }) {
  const { t_ } = useSmartTranslation()
  const [blobUrl, setBlobUrl] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return undefined
    let isCurrent = true
    let currentUrl = ''
    setError('')
    setBlobUrl('')
    fetchAcceptanceDocumentContent(contentPath)
      .then(({ blob }) => {
        if (!isCurrent) return
        currentUrl = window.URL.createObjectURL(blob)
        setBlobUrl(currentUrl)
      })
      .catch(() => {
        if (isCurrent) setError(t_('The document could not be loaded.'))
      })
    return () => {
      isCurrent = false
      if (currentUrl) window.URL.revokeObjectURL(currentUrl)
    }
    // t_ intentionally excluded: the mocked translation hook returns a fresh function reference on
    // every render, which would otherwise re-trigger this fetch effect forever (same convention as
    // the rest of this module — see FabricationOrderAcceptanceForm's form-load effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contentPath])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ height: '75vh', p: 0 }}>
        {error && <Alert severity='error' sx={{ m: 2 }}>{error}</Alert>}
        {!error && !blobUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {!error && blobUrl && (
          <iframe title={title} src={blobUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t_('Close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
